import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../../../context/AuthContext';
import { useTimezone } from '../../../../hooks/useTimezone';
import { formatPaisa, toSignedPaisa } from '../../../../utils/paisa';

/**
 * The statement a reconciliation works to: its closing date and balance.
 * Used to start one and to correct it while it is open.
 */
const StatementDialog: React.FC<{
  open: boolean;
  title: string;
  subtitle: string;
  initial?: { statementDate: string; statementBalancePaisa: number };
  /** The last reconciled statement — the new one must be later. */
  after?: string | null;
  submitLabel: string;
  onClose: () => void;
  onSubmit: (statement: { statementDate: string; statementBalancePaisa: number }) => Promise<void>;
}> = ({ open, title, subtitle, initial, after, submitLabel, onClose, onSubmit }) => {
  const { currency } = useAuth();
  const { getToday, formatDate } = useTimezone();
  const [date, setDate] = useState('');
  const [balance, setBalance] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDate(initial?.statementDate ?? getToday());
    setBalance(initial ? String(initial.statementBalancePaisa / 100) : '');
    setError(null);
  }, [open, initial, getToday]);

  const paisa = toSignedPaisa(balance);
  const tooEarly = !!after && !!date && date <= after;
  const canSubmit = !busy && !!date && paisa != null && !tooEarly;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ statementDate: date, statementBalancePaisa: paisa! });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'The statement could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {title}
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Stack spacing={2}>
          <TextField
            label="Statement date"
            type="date"
            size="small"
            value={date}
            onChange={e => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: getToday() }}
            error={tooEarly}
            helperText={tooEarly ? `Reconciled to ${formatDate(after!)} already — choose a later date.` : 'The last day the statement covers.'}
            disabled={busy}
          />
          <TextField
            label="Closing balance on the statement"
            size="small"
            value={balance}
            onChange={e => setBalance(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
            error={balance.trim() !== '' && paisa == null}
            helperText={balance.trim() !== '' && paisa == null
              ? 'Enter an amount with up to two decimals.'
              : paisa != null && paisa < 0 ? `Overdrawn by ${currency} ${formatPaisa(-paisa)}` : 'Use a minus sign if the account is overdrawn.'}
            disabled={busy}
            autoFocus={!initial}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={!canSubmit}>{busy ? 'Saving…' : submitLabel}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatementDialog;
